import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { projectApiUrl } from "../../constants";
import { uploadAttachment, UploadedAttachment } from "../../storageUpload";
import VerificationDocument from "../VerificationDocument/VerificationDocument";

const types = ["government_certificate", "bank_statement", "utility_bill"] as const;
const allowedMimeTypes = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
const headers = { "X-Requested-With": "XMLHttpRequest" };

type Props = { residenceCode: string; identityVerified: boolean; onVerifiedCodeChange?: (code: string) => void };

/** Uses the saved country, never an unsaved draft or the identity KYC status. */
export default function ResidenceVerification({ residenceCode, identityVerified, onVerifiedCodeChange }: Props) {
  const { t } = useTranslation();
  const [residence, setResidence] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [consent, setConsent] = useState(false);
  const [evidenceType, setEvidenceType] = useState<string>(() => {
    try { const saved = sessionStorage.getItem("pd:residence-evidence-type"); return types.find(type => type === saved) || types[0]; }
    catch { return types[0]; }
  });
  const uploaded = useRef<{ file: File; attachment: UploadedAttachment } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const busy = useRef(false);
  const endpoint = `${projectApiUrl}/personal_data/residence-verification`;

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setResidence(null); setError(""); onVerifiedCodeChange?.("");
    axios.get(endpoint, { headers, withCredentials: true, signal: controller.signal })
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        setResidence(data.residence);
        const v = data.residence?.verification;
        const valid = v?.status === "verified" && v.verifiedCountry === data.residence.country
          && (v.scope === "country_region" ? v.verifiedRegion === data.residence.region : !data.residence.region)
          && (!v.expiresAt || Date.parse(v.expiresAt) > Date.now());
        onVerifiedCodeChange?.(valid ? data.residence.region || data.residence.country : "");
      })
      .catch(() => { if (!controller.signal.aborted) setError(t("personalData.accountVerification.submitFailed")); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [endpoint, residenceCode, t, onVerifiedCodeChange]);

  useEffect(() => {
    if (!file && !saving) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [file, saving]);

  const verification = residence?.verification || {};
  const status = verification.status || "not_submitted";
  const locked = status === "pending" || status === "verified";
  const ready = !loading && !saving && !locked && identityVerified && Boolean(residenceCode) && Boolean(file) && consent;
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!ready || !file || busy.current) return;
    busy.current = true; setSaving(true); setError("");
    try {
      let attachment = uploaded.current?.file === file ? uploaded.current.attachment : null;
      if (!attachment) {
        attachment = await uploadAttachment(file, "kyc_document", "residence.verification.evidenceRef");
        uploaded.current = { file, attachment };
      }
      const { data } = await axios.post(endpoint, {
        evidenceRef: attachment.fileId, evidenceType, consentAccepted: true,
      }, { headers, withCredentials: true });
      setResidence(data.residence); onVerifiedCodeChange?.(""); setFile(null); setConsent(false); uploaded.current = null;
      if (inputRef.current) inputRef.current.value = "";
      try { sessionStorage.removeItem("pd:residence-evidence-type"); } catch { /* storage may be disabled */ }
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || t("personalData.accountVerification.submitFailed"));
    } finally { busy.current = false; setSaving(false); }
  };

  return <section className={`pd-residence-verification pd-account-verification-card pd-glass ${status === "verified" ? "is-verified" : status === "pending" ? "is-pending" : ""}`} aria-labelledby="pd-residence-title" aria-busy={loading || saving}>
    <div className="pd-account-verification-card__header"><div>
      <h2 id="pd-residence-title">{t("personalData.residenceVerification.title")}</h2>
      <p>{t("personalData.residenceVerification.description")}</p>
      <p role="status">{t(loading ? "personalData.accountVerification.documentLoading" : status === "verified" ? "personalData.verification.verified" : status === "pending" ? "personalData.accountVerification.pendingTitle" : status === "rejected" ? "personalData.verification.rejected" : "personalData.accountVerification.notSubmittedTitle")}</p>
      {residence?.country && <p>{residence.country}{residence.region ? ` / ${residence.region}` : ""}</p>}
    </div></div>
    <div className="pd-account-verification-card__body">
      <p>{t("personalData.categories.documents")}</p>
      <ul>{types.map(type => <li key={type}>{t(`personalData.residenceVerification.types.${type}`)}</li>)}</ul>
      <p>{t("personalData.residenceVerification.requirements")}</p>
      {verification.reviewReason && <p>{verification.reviewReason}</p>}
      {verification.evidenceRef && <VerificationDocument fileId={verification.evidenceRef} fileName={t(`personalData.residenceVerification.types.${verification.evidenceType}`)} />}
      {error && <p role="alert">{error}</p>}
      {!locked && <form onSubmit={submit}>
        <fieldset disabled={loading || saving || !identityVerified || !residenceCode}>
          <label>{t("personalData.categories.documents")}
            <select value={evidenceType} onChange={event => {
              setEvidenceType(event.target.value);
              try { sessionStorage.setItem("pd:residence-evidence-type", event.target.value); } catch { /* optional draft */ }
            }}>{types.map(type => <option key={type} value={type}>{t(`personalData.residenceVerification.types.${type}`)}</option>)}</select>
          </label>
          <label>{t("personalData.accountVerification.viewDocument")}
            <input ref={inputRef} type="file" accept={allowedMimeTypes.join(",")} onChange={event => {
              const selected = event.target.files?.[0];
              if (selected && (!allowedMimeTypes.includes(selected.type) || selected.size <= 0 || selected.size > 10 * 1024 * 1024)) {
                setError(t("personalData.residenceVerification.requirements")); event.target.value = ""; setFile(null); uploaded.current = null; return;
              }
              setError(""); setFile(selected || null); uploaded.current = null;
            }} />
          </label>
          <label><input type="checkbox" checked={consent} onChange={event => setConsent(event.target.checked)} />{t("personalData.residenceVerification.consent")}</label>
          <button type="submit" className="pd-primary-button" disabled={!ready}>{t("personalData.accountVerification.submit")}</button>
        </fieldset>
      </form>}
    </div>
  </section>;
}
